import {Component, ElementRef, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {
    AuthenticationMethods,
    BackofficeLightDto,
    ImpulseDurationUnit,
    MeasuringPointLightDto,
    MeasuringPointService,
    MeasuringPointType,
    CustomRoleDto,
    SearchMeasuringPointResultDto,
    SearchCustomerDto,
    ServiceCategoryDto,
    ServiceCategoryService,
    ServiceConfigDto,
    ServiceDto,
    ServiceInputDto,
    ServiceService,
    ServiceType,
    CustomerLightDto
} from '../../_services/configuration-services';
import {ActivatedRoute, Router} from '@angular/router';
import {NgbModal, NgbModalConfig} from '@ng-bootstrap/ng-bootstrap';
import {firstValueFrom, Subscription} from 'rxjs';
import {faQrcode, faTrash, faUserEdit, faUserPlus, faUserSlash} from '@fortawesome/free-solid-svg-icons';
import {AppConstants, PermissionKeys} from '../../app.constants';
import {TranslateService} from '@ngx-translate/core';
import {CustomerConfigService} from '../../_shared/customer-config-service';
import {NotificationsService} from '../../_shared/notifications.service';
import {
    AssignServiceMeasuringPointModalComponent
} from './assign-service-measuring-point-modal/assign-service-measuring-point-modal.component';
import {RolesService} from '../../_shared/roles-service';
import {FormUtils} from '../../_shared/form-utils';
import {DateUtils} from '../../_shared/date-utils';
import {ConfirmModalService} from '../../_shared/_components/confirm-modal/confirm-modal.component';
import {TranslateUtils} from '../../_shared/translate-utils';
import {FormControl, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {ServiceQrCodeViewComponent} from './service-qr-code-view/service-qr-code-view.component';
import {IDropdownSettings} from 'ng-multiselect-dropdown';
import {BackofficePermissionsService} from "../../_shared/backoffice-permissions.service";

@Component({
    selector: 'app-manage-service',
    templateUrl: './manage-service.component.html',
    providers: [NgbModalConfig, NgbModal],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ManageServiceComponent implements OnInit, OnDestroy {

    FormUtils = FormUtils;
    ServiceType = ServiceType;
    PermissionKeys = PermissionKeys;

    serviceForm: UntypedFormGroup;

    serviceId: string | null = null;
    service: ServiceDto | null;
    serviceConfig!: ServiceConfigDto;

    categories: ServiceCategoryDto[];
    impulseDurationUnits = Object.keys(ImpulseDurationUnit)
        .filter(unit => ImpulseDurationUnit[unit] !== ImpulseDurationUnit.Millisecond);
    hoursOfDay: string[] = [];

    dateFormat = DateUtils.dateFormat;

    supportedImage = AppConstants.supportedImageType;
    imageMaxSizeInKB = AppConstants.imageMaxSizeInKB;
    imageToUpload: File | null = null;
    imageUrl: string | null = null;
    imageValid = true;

    // icons
    icons = {
        userSlash: faUserSlash,
        userEdit: faUserEdit,
        userPlus: faUserPlus,
        trash: faTrash,
        qrcode: faQrcode
    }

    // authentication methods Mutli-select
    authenticationMethodDropdownSources = [];
    authenticationMethodDropdownSettings: IDropdownSettings = {};

    // exploitants multi-select
    exploitantsDropdownSources: { itemId: string; itemVal: string }[] = [];
    exploitantsDropdownSettings: IDropdownSettings = {};
    exploitantCandidates: BackofficeLightDto[] = [];

    // measuring points
    customerMeasuringPoints: Array<MeasuringPointLightDto> = new Array<MeasuringPointLightDto>();
    adminMeasuringPoints: Array<MeasuringPointLightDto> = new Array<MeasuringPointLightDto>();

    // Used to know if we need to display the filters after a search
    keepDisplayCustomerMeasuringPointTable = false;

    searchMeasuringPointCustomerId = '';
    searchAdminMeasuringPointId = '';
    searchCustomerMeasuringPointId = '';

    adminMeasuringPointType = MeasuringPointType.Admin;
    customerMeasuringPointType = MeasuringPointType.Customer;
    // ---------------

    @ViewChild('serviceChildrenSection', {static: false}) serviceChildrenSection: ElementRef;

    private langChangeSubscription$: Subscription;
    crtLang = TranslateUtils.defaultLanguage;

    constructor(private readonly serviceService: ServiceService,
                private readonly categoryService: ServiceCategoryService,
                public readonly customerConfigService: CustomerConfigService,
                private readonly route: ActivatedRoute,
                private readonly notificationsService: NotificationsService,
                private readonly confirmService: ConfirmModalService,
                private readonly modalService: NgbModal,
                private readonly translateService: TranslateService,
                private readonly router: Router,
                public readonly rolesService: RolesService,
                private readonly measuringPointService: MeasuringPointService,
                private readonly backofficePermissionsService: BackofficePermissionsService) {

        this.serviceForm = new UntypedFormGroup({
            serviceId: new UntypedFormControl({value: null, disabled: true}),
            serviceState: new UntypedFormControl(false, [Validators.required]),
            category: new UntypedFormControl(null, [Validators.required]),
            code: new UntypedFormControl(null, [Validators.required]),
            nameFR: new UntypedFormControl(null, [Validators.required]),
            nameDE: new UntypedFormControl(null),
            nameIT: new UntypedFormControl(null),
            nameEN: new UntypedFormControl(null),
            descriptionFR: new UntypedFormControl(null),
            descriptionDE: new UntypedFormControl(null),
            descriptionIT: new UntypedFormControl(null),
            descriptionEN: new UntypedFormControl(null),
            instructionFR: new UntypedFormControl(null),
            instructionDE: new UntypedFormControl(null),
            instructionIT: new UntypedFormControl(null),
            instructionEN: new UntypedFormControl(null),
            authenticationMethod: new UntypedFormControl(null),
            selectedExploitants: new UntypedFormControl([]),
            minimalWalletBalanceRequired: new UntypedFormControl(0, [Validators.required]),
            customRoles: new FormControl<CustomRoleDto[] | null>(null)
        });
    }

    ngOnInit(): void {
        this.crtLang = this.translateService.currentLang;
        this.langChangeSubscription$ = this.translateService.onLangChange.subscribe(_ => {
            this.crtLang = this.translateService.currentLang;
            this.populateAuthenticationMethodsSelect();
            this.refreshExploitantsDropdownSettings();
        });

        this.serviceId = this.route.snapshot.params['serviceId'] ?? null

        this.populateAuthenticationMethodsSelect();
        this.refreshExploitantsDropdownSettings();

        const hasGestionServices = this.hasGestionServicesPermission();

        // Two populations reach the editable form:
        //   - users with the gestionServices permission — can create or edit any service
        //   - users without that permission, but exploitant of the service in the route —
        //     can only edit the service they're assigned to; ServiceEditGuard has already
        //     verified the membership for the route's serviceId before we get here.
        if (hasGestionServices || this.serviceId) {
            this.hoursOfDay = DateUtils.generateHoursOfDayBy30Minutes();

            this.categoryService.getCategories().pipe().subscribe(categories => {
                this.categories = categories;
            });

            // The Exploitants section of the form is rendered only for users with the
            // gestionServices permission; the candidates list backs that section's dropdown,
            // so it's loaded only when the section is visible.
            if (hasGestionServices) {
                this.loadExploitantCandidates();
            }

            if (this.serviceId) { // Edit a service
                void this.fetchService();
            } else if (history.state.data) { // Create a new service
                this.serviceConfig = ServiceConfigDto.fromJS(history.state.data);
                this.setupForm();
                this.addSpecificControlsForService();
            } else {
                // Return to service list in case of a refresh on an unsaved service
                void this.backToServiceList();
            }
        }
    }

    ngOnDestroy(): void {
        this.langChangeSubscription$.unsubscribe();
        this.modalService.dismissAll('destroy');
    }

    setupForm(): void {
        if (this.canConfigureBookable()) {
            this.serviceForm.addControl('bookable', new FormControl<boolean>(true, [Validators.required]));
            this.bookable?.disable();
        }

        if (this.service) {
            this.serviceIdForm.setValue(this.service.serviceId);
            this.serviceState.setValue(this.service.active);
            this.bookable?.setValue(true);
            this.category.setValue(this.service.categoryId);
            this.code.setValue(this.service.code);
            this.nameFR.setValue(this.service.nameFR);
            this.nameDE.setValue(this.service.nameDE);
            this.nameIT.setValue(this.service.nameIT);
            this.nameEN.setValue(this.service.nameEN);
            this.descriptionFR.setValue(this.service.descriptionFR);
            this.descriptionDE.setValue(this.service.descriptionDE);
            this.descriptionIT.setValue(this.service.descriptionIT);
            this.descriptionEN.setValue(this.service.descriptionEN);
            this.instructionFR.setValue(this.service.instructionFR);
            this.instructionDE.setValue(this.service.instructionDE);
            this.instructionIT.setValue(this.service.instructionIT);
            this.instructionEN.setValue(this.service.instructionEN);
            this.minimalWalletBalanceRequired.setValue(this.service.minimalWalletBalanceRequired);

            const selectedAuthMethods = [];
            this.service.authenticationMethods.filter(r => r !== AuthenticationMethods.None)
                .forEach(r => selectedAuthMethods.push({
                    itemId: r,
                    itemVal: this.translateService.instant('common.authenticationMethod.' + r)
                }));
            this.authenticationMethod.patchValue(selectedAuthMethods);

            this.prefillSelectedExploitants();
            this.customRolesFormControl.setValue(this.service.customRoles ?? null);
        }
    }

    createOrEditService(): void {
        const serviceInput = new ServiceInputDto({
            serviceConfigId: this.serviceConfig.id,
            active: this.serviceState.value,
            categoryId: this.category.value,
            code: this.code.value,
            bookable: this.bookable?.value ?? false,
            nameFR: this.nameFR.value,
            nameDE: this.nameDE.value,
            nameIT: this.nameIT.value,
            nameEN: this.nameEN.value,
            descriptionFR: this.descriptionFR.value,
            descriptionDE: this.descriptionDE.value,
            descriptionIT: this.descriptionIT.value,
            descriptionEN: this.descriptionEN.value,
            instructionFR: this.instructionFR.value,
            instructionDE: this.instructionDE.value,
            instructionIT: this.instructionIT.value,
            instructionEN: this.instructionEN.value,
            authenticationMethods: this.authenticationMethod.value?.map(item => item.itemId),
            backofficeIds: this.buildBackofficeIdsPayload(),
            minimalWalletBalanceRequired: this.minimalWalletBalanceRequired.value,
        });

        if (!serviceInput.authenticationMethods || serviceInput.authenticationMethods?.length < 1) {
            serviceInput.authenticationMethods = [AuthenticationMethods.None];
        }

        if (this.canConfigureRoles()) {
            serviceInput.customRoles = this.customRolesFormControl.value;
        }

        if (this.canConfigureImpulseDuration()) {
            serviceInput.impulseDuration = this.impulseDuration.value;
            serviceInput.impulseDurationUnit = this.impulseDurationUnit.value;
            serviceInput.maxImpulseDuration = this.maxImpulseDuration?.value;
        }

        if (this.canConfigureAmountToReserveForServiceConsumption()) {
            serviceInput.amountToReserveForServiceConsumption = this.amountToReserveForServiceConsumption.value;
        }

        if (this.serviceId) {
            this.serviceService.updateService(this.service.serviceId, serviceInput).pipe().subscribe(value => {
                this.service = value;
                this.notificationsService.success({title: 'services.manage.notifications.updateSuccess'});
            });

        } else {
            this.serviceService.createService(serviceInput).pipe().subscribe(value => {
                this.notificationsService.success({title: 'services.manage.notifications.addSuccess'});
                this.service = value;
                this.serviceId = this.service.serviceId;

                // Scroll down the page to display the children forms
                setTimeout(() => this.serviceChildrenSection.nativeElement.scrollIntoView(), 0);
            });
        }
    }

    async backToServiceList(): Promise<void> {
        if (this.service?.serviceId) {
            await this.router.navigate(['/services/' + this.service.serviceId]);

        } else {
            await this.router.navigate(['/services'], {replaceUrl: true});
        }
    }

    async deleteService(): Promise<void> {
        if (this.serviceId) {
            const result = await this.confirmService.confirm({titleKey: 'common.confirmModal.title.delete'});
            if (result === ConfirmModalService.yes) {
                await firstValueFrom(this.serviceService.deleteService(this.service.serviceId));
                this.notificationsService.success({title: 'services.manage.notifications.deleteSuccess'});
                await this.router.navigate(['/services'], {relativeTo: this.route});
            }
        }
    }

    fileSelected(files: FileList): void {
        if (files.length > 0) {
            this.imageToUpload = files[0];
            const imgSize = this.imageToUpload.size;
            if ((imgSize / 1024) > AppConstants.imageMaxSizeInKB) {
                this.imageValid = false;
                this.imageToUpload = null;
            } else {
                this.imageValid = true;
            }
        }
    }

    uploadImage(): void {
        this.serviceService.uploadServiceImage(this.service.serviceId, {
            data: this.imageToUpload,
            fileName: this.imageToUpload.name
        }).pipe().subscribe(value => {
            this.imageUrl = value.imageUrl;
            this.imageToUpload = null;
        });
    }

    openQrCode(adminMeasuringPoint): void {
        const modal = this.modalService.open(ServiceQrCodeViewComponent, {centered: true});
        modal.componentInstance.serviceId = this.serviceId;
        modal.componentInstance.qrRawData = adminMeasuringPoint.id;
        modal.componentInstance.qrName = adminMeasuringPoint['name' + this.crtLang.toUpperCase()];
    }

    isFormValid(): boolean {
        if (!this.canConfigureRoles()) {
            return true;
        }
        return this.customRolesFormControl.valid;
    }

    // region MeasuringPoints
    async getMeasuringPoints(): Promise<void> {
        if (this.isCurrentBackofficeServiceExploitant()) {
            this.customerMeasuringPoints = await firstValueFrom(this.measuringPointService.get20FirstForService(
                this.serviceId,
                this.searchCustomerMeasuringPointId,
                this.searchMeasuringPointCustomerId,
                this.crtLang,
                this.customerMeasuringPointType));
        }

        if (this.rolesService.hasRoleSuperAdmin()) {
            this.adminMeasuringPoints = await firstValueFrom(this.measuringPointService.get20FirstForService(
                this.serviceId,
                this.searchAdminMeasuringPointId,
                '',
                this.crtLang,
                this.adminMeasuringPointType));
        }
    }

    async searchByCustomerMeasuringPointId(measuringPoint: SearchMeasuringPointResultDto | null): Promise<void> {
        this.searchCustomerMeasuringPointId = measuringPoint?.id ?? '';
        await this.getMeasuringPoints();
    }

    async searchByAdminMeasuringPointId(measuringPoint: SearchMeasuringPointResultDto | null): Promise<void> {
        this.searchAdminMeasuringPointId = measuringPoint?.id ?? '';
        await this.getMeasuringPoints();
    }

    async searchByCustomerId(customer: SearchCustomerDto | null): Promise<void> {
        this.keepDisplayCustomerMeasuringPointTable = true;
        this.searchMeasuringPointCustomerId = customer?.id ?? '';
        await this.getMeasuringPoints();
    }

    async unassignMeasuringPointFromService(measuringPoint: MeasuringPointLightDto): Promise<void> {
        const result = await this.confirmService.confirm({titleKey: 'measuringPoints.unassignConfirmModal.title'});

        if (result === ConfirmModalService.yes) {
            await firstValueFrom(this.measuringPointService.unassignCurrentServiceFromMeasuringPoint(this.serviceId, measuringPoint.id));
            this.notificationsService.success({title: 'measuringPoints.notifications.unassignSuccess'});
            await this.getMeasuringPoints();
        }
    }

    assignNewMeasuringPoint(measuringPointType: MeasuringPointType): void {
        const modal = this.modalService.open(AssignServiceMeasuringPointModalComponent, {centered: true});
        modal.componentInstance.serviceId = this.serviceId;
        modal.componentInstance.measuringPointTypeToAdd = measuringPointType;
        modal.componentInstance.editMode = false;
        modal.result
            .then(async value => {
                if (value === 'success') {
                    await this.getMeasuringPoints();
                }
            }, () => { /* catch the rejection */ });
    }

    printMeasuringPointCustomer(customer: CustomerLightDto | null): string {
        if (customer == null) {
            return '';
        } else {
            return `${customer.lastName} ${customer.firstName}`;
        }
    }

    printMeasuringPointName(measuringPoint: MeasuringPointLightDto | null): string {
        if (!measuringPoint) {
            return '';
        }
        return `${measuringPoint['name' + this.crtLang.toUpperCase()]}`;
    }

    editMeasuringPointCustomerAssignation(measuringPoint: MeasuringPointLightDto): void {
        const modal = this.modalService.open(AssignServiceMeasuringPointModalComponent, {centered: true});
        modal.componentInstance.serviceId = this.serviceId;
        modal.componentInstance.measuringPoint = measuringPoint;
        modal.componentInstance.customer = measuringPoint.customer;
        modal.componentInstance.measuringPointTypeToAdd = MeasuringPointType.Customer;
        modal.componentInstance.editMode = true;
        modal.result
            .then(async value => {
                if (value === 'success') {
                    await this.getMeasuringPoints();
                }
            }, () => { /* catch the rejection */ });
    }

    async unassignMeasuringPointFromCustomer(measuringPoint: MeasuringPointLightDto): Promise<void> {
        const result = await this.confirmService.confirm({
            titleKey: 'common.confirmModal.title.unassign'
        });

        if (result === ConfirmModalService.yes) {
            await firstValueFrom(this.measuringPointService.unassignCurrentCustomerFromMeasuringPoint(this.serviceId, measuringPoint.id));

            this.notificationsService.success({title: 'measuringPoints.notifications.unassignCustomerSuccess'});
            await this.getMeasuringPoints();
        }
    }

    // endRegion

    hasGestionServicesPermission(): boolean {
        return this.backofficePermissionsService.hasPermission(PermissionKeys.gestionServices);
    }

    isCurrentBackofficeServiceExploitant(): boolean {
        // Mirror the backend AuthorizeExploitantMiddleware: SuperAdmin bypasses the exploitant check
        if (this.rolesService.hasRoleSuperAdmin()) {
            return true;
        }
        const currentBackofficeId = this.backofficePermissionsService.getCurrentBackofficeId();
        if (!currentBackofficeId || !this.service?.backoffices) {
            return false;
        }
        return this.service.backoffices.some(b => b.id === currentBackofficeId);
    }

    hasServiceEditAccess(): boolean {
        return this.hasGestionServicesPermission() || this.isCurrentBackofficeServiceExploitant();
    }

    isMooringServiceType(): boolean {
        return this.serviceConfig.serviceType === ServiceType.MooringPlace;
    }

    isAccessControlServiceType(): boolean {
        return this.serviceConfig.serviceType === ServiceType.AccessControl;
    }

    canConfigureMap(): boolean {
        return this.serviceConfig.serviceType === ServiceType.MooringPlace
            || this.serviceConfig.serviceType === ServiceType.PublicLighting;
    }

    canConfigureAvailabilityRanges(): boolean {
        return this.serviceConfig.serviceType !== ServiceType.Weather
            && this.serviceConfig.serviceType !== ServiceType.Webcam
            && this.serviceConfig.serviceType !== ServiceType.PublicLighting;
    }

    canConfigureBookable(): boolean {
        return this.serviceConfig.serviceType === ServiceType.Bookable;
    }

    canConfigureTariffs(): boolean {
        return this.serviceConfig.serviceType !== ServiceType.MooringPlace
            && this.serviceConfig.serviceType !== ServiceType.PointOfSale
            && this.serviceConfig.serviceType !== ServiceType.Weather
            && this.serviceConfig.serviceType !== ServiceType.Webcam
            && this.serviceConfig.serviceType !== ServiceType.PublicLighting;
    }

    canConfigureAdminMeasuringPoint(): boolean {
        return this.serviceConfig.serviceType === ServiceType.Bookable
            || this.serviceConfig.serviceType === ServiceType.Energy
            || this.serviceConfig.serviceType === ServiceType.TimeLimited
            || this.serviceConfig.serviceType === ServiceType.Weather;
    }

    canConfigureCustomerMeasuringPoint(): boolean {
        return this.serviceConfig.serviceType === ServiceType.Energy ||
            this.serviceConfig.serviceType === ServiceType.UserAlarm;
    }

    canConfigureImpulseDuration(): boolean {
        return this.serviceConfig.serviceType === ServiceType.TimeLimited
            || this.serviceConfig.serviceType === ServiceType.Bookable;
    }

    canConfigureRoles(): boolean {
        return this.serviceConfig.serviceType !== ServiceType.MooringPlace
            && this.serviceConfig.serviceType !== ServiceType.PublicLighting;
    }

    canConfigurePublicRole(): boolean {
        const publicServiceTypes = [
            ServiceType.Default,
            ServiceType.Bookable,
            ServiceType.TimeLimited,
            ServiceType.CraningAndWinterizing,
            ServiceType.Webcam,
            ServiceType.Weather,
            ServiceType.ChargingStations,
        ]
        return publicServiceTypes.indexOf(this.serviceConfig.serviceType) >= 0;
    }

    /**
     * Minimal Wallet balance is unavailable for :
     *  - Hydrowash
     *  - Default
     *  - MooringPlace
     */
    canConfigureMinimalWalletBalance(): boolean {
        if (this.customerConfigService.isEwalletEnabled()) {
            return !(this.serviceConfig.serviceType === ServiceType.Hydrowash
                || this.serviceConfig.serviceType === ServiceType.Default
                || this.serviceConfig.serviceType === ServiceType.MooringPlace);
        }
        return false;
    }

    canConfigureAmountToReserveForServiceConsumption(): boolean {
        return this.serviceConfig.serviceType === ServiceType.ChargingStations;
    }

    canConfigureActivation(): boolean {
        return this.serviceConfig.serviceType !== ServiceType.PublicLighting;
    }

    private addSpecificControlsForService(): void {
        if (this.canConfigureImpulseDuration()) {
            this.serviceForm.addControl('impulseDuration', new UntypedFormControl(this.service?.impulseDuration));
            this.serviceForm.addControl('impulseDurationUnit', new UntypedFormControl(this.service?.impulseDurationUnit));

            if (this.serviceConfig.serviceType === ServiceType.TimeLimited) {
                this.impulseDuration.setValidators(Validators.required);
                this.serviceForm.addControl('maxImpulseDuration', new UntypedFormControl(this.service?.maxImpulseDuration));
                this.maxImpulseDuration.setValidators([Validators.required, Validators.min(this.impulseDuration.value)]);
                this.impulseDurationUnit.setValidators(Validators.required);
            }
        }

        if (this.canConfigureAmountToReserveForServiceConsumption()) {
            this.serviceForm.addControl('amountToReserveForServiceConsumption', new UntypedFormControl(this.service?.amountToReserveForServiceConsumption, [Validators.required, Validators.min(0), FormUtils.integerPatternValidator]));
        }
    }

    private loadExploitantCandidates(): void {
        this.serviceService.getExploitantCandidates().subscribe(backoffices => {
            this.exploitantCandidates = backoffices ?? [];
            this.exploitantsDropdownSources = this.exploitantCandidates.map(b => ({
                itemId: b.id,
                itemVal: this.formatBackofficeLabel(b)
            }));
            this.prefillSelectedExploitants();
        });
    }

    private formatBackofficeLabel(backoffice: BackofficeLightDto): string {
        return `${backoffice.firstName} ${backoffice.lastName} — ${backoffice.email}`;
    }

    private refreshExploitantsDropdownSettings(): void {
        this.exploitantsDropdownSettings = {
            idField: 'itemId',
            textField: 'itemVal',
            singleSelection: false,
            selectAllText: this.translateService.instant('common.multiselect.selectAll'),
            unSelectAllText: this.translateService.instant('common.multiselect.unselectAll'),
            searchPlaceholderText: this.translateService.instant('common.multiselect.search'),
            noDataAvailablePlaceholderText: this.translateService.instant('AucunExploitantAssigne'),
            itemsShowLimit: 5,
            allowSearchFilter: true
        };
    }

    /**
     * Returns the backofficeIds to send in the update payload.
     *  - Admin (gestionServices): list edited via the form.
     *  - Exploitant (no permission): echo back service.backoffices unchanged. The exploitants
     *    section is hidden in that case, so the form value is empty/stale; the backend rejects
     *    any change to this list from a non-admin caller (ExploitantCannotModifyAdminFields).
     */
    private buildBackofficeIdsPayload(): string[] {
        if (this.hasGestionServicesPermission()) {
            return (this.selectedExploitants.value as { itemId: string }[] | null)?.map(item => item.itemId) ?? [];
        }
        return this.service?.backoffices?.map(b => b.id) ?? [];
    }

    private prefillSelectedExploitants(): void {
        if (!this.service?.backoffices || this.exploitantsDropdownSources.length === 0) {
            return;
        }
        const sourceIds = new Set(this.exploitantsDropdownSources.map(s => s.itemId));
        const selected = this.service.backoffices
            .filter(b => sourceIds.has(b.id))
            .map(b => ({
                itemId: b.id,
                itemVal: this.formatBackofficeLabel(b)
            }));
        this.selectedExploitants.patchValue(selected);
    }

    private populateAuthenticationMethodsSelect() {
        this.authenticationMethodDropdownSources = [];
        Object.keys(AuthenticationMethods).filter(r => r !== AuthenticationMethods.None)
            .forEach(r => {
                this.authenticationMethodDropdownSources.push({
                    itemId: r,
                    itemVal: this.translateService.instant('common.authenticationMethod.' + r)
                })
            });
        this.authenticationMethodDropdownSettings = {
            idField: 'itemId',
            textField: 'itemVal',
            singleSelection: false,
            selectAllText: this.translateService.instant('common.multiselect.selectAll'),
            unSelectAllText: this.translateService.instant('common.multiselect.unselectAll'),
            searchPlaceholderText: this.translateService.instant('common.multiselect.search'),
            noDataAvailablePlaceholderText: this.translateService.instant('common.multiselect.noResults'),
            itemsShowLimit: 3,
            allowSearchFilter: false
        };
    }

    private async fetchService(): Promise<void> {
        const service = await firstValueFrom(this.serviceService.getService(this.serviceId));
        this.service = service;
        this.serviceConfig = service.serviceConfig;
        this.imageUrl = service.imageUrl;

        this.setupForm();
        this.addSpecificControlsForService();

        if (this.canConfigureAdminMeasuringPoint() || this.canConfigureCustomerMeasuringPoint()) {
            await this.getMeasuringPoints();
        }
    }

    public get serviceType(): typeof ServiceType {
        return ServiceType;
    }

    get serviceIdForm(): UntypedFormControl {
        return this.serviceForm.get('serviceId') as UntypedFormControl;
    }

    get customRolesFormControl(): FormControl<CustomRoleDto[] | null> {
        return this.serviceForm.get('customRoles') as FormControl<CustomRoleDto[] | null>;
    }

    get serviceState(): UntypedFormControl {
        return this.serviceForm.get('serviceState') as UntypedFormControl;
    }

    get category(): UntypedFormControl {
        return this.serviceForm.get('category') as UntypedFormControl;
    }

    get code(): UntypedFormControl {
        return this.serviceForm.get('code') as UntypedFormControl;
    }

    get nameFR(): UntypedFormControl {
        return this.serviceForm.get('nameFR') as UntypedFormControl;
    }

    get nameDE(): UntypedFormControl {
        return this.serviceForm.get('nameDE') as UntypedFormControl;
    }

    get nameIT(): UntypedFormControl {
        return this.serviceForm.get('nameIT') as UntypedFormControl;
    }

    get nameEN(): UntypedFormControl {
        return this.serviceForm.get('nameEN') as UntypedFormControl;
    }

    get descriptionFR(): UntypedFormControl {
        return this.serviceForm.get('descriptionFR') as UntypedFormControl;
    }

    get descriptionDE(): UntypedFormControl {
        return this.serviceForm.get('descriptionDE') as UntypedFormControl;
    }

    get descriptionIT(): UntypedFormControl {
        return this.serviceForm.get('descriptionIT') as UntypedFormControl;
    }

    get descriptionEN(): UntypedFormControl {
        return this.serviceForm.get('descriptionEN') as UntypedFormControl;
    }

    get instructionFR(): UntypedFormControl {
        return this.serviceForm.get('instructionFR') as UntypedFormControl;
    }

    get instructionDE(): UntypedFormControl {
        return this.serviceForm.get('instructionDE') as UntypedFormControl;
    }

    get instructionIT(): UntypedFormControl {
        return this.serviceForm.get('instructionIT') as UntypedFormControl;
    }

    get instructionEN(): UntypedFormControl {
        return this.serviceForm.get('instructionEN') as UntypedFormControl;
    }

    get bookable(): FormControl<boolean> | null {
        return this.serviceForm.get('bookable') as FormControl<boolean> ?? null;
    }

    get authenticationMethod(): UntypedFormControl {
        return this.serviceForm.get('authenticationMethod') as UntypedFormControl;
    }

    get selectedExploitants(): UntypedFormControl {
        return this.serviceForm.get('selectedExploitants') as UntypedFormControl;
    }

    get minimalWalletBalanceRequired(): UntypedFormControl {
        return this.serviceForm.get('minimalWalletBalanceRequired') as UntypedFormControl;
    }

    get amountToReserveForServiceConsumption(): UntypedFormControl {
        return this.serviceForm.get('amountToReserveForServiceConsumption') as UntypedFormControl;
    }

    get impulseDuration(): UntypedFormControl {
        return this.serviceForm.get('impulseDuration') as UntypedFormControl;
    }

    get maxImpulseDuration(): UntypedFormControl | null {
        return this.serviceForm.get('maxImpulseDuration') as UntypedFormControl;
    }

    get impulseDurationUnit(): UntypedFormControl {
        return this.serviceForm.get('impulseDurationUnit') as UntypedFormControl;
    }
}
